import {useWbAgentClient} from '@wandb/weave/lib/WbAgent/useWbAgentClient';
import type {TurnResponse} from '@wandb/weave/lib/WbAgent/WbAgentClient';
import {useCallback, useEffect, useRef, useState} from 'react';

import {extractErrorMessage} from '../../../agentChat/runtime/errors';
import {useNavContext} from '../../NavContextProvider';
import {decodePromptForThreadTitle} from '../utils';

const PAGE_SIZE = 50;

export interface ThreadHistoryItem {
  rootTurnId: string;
  title: string;
  updatedAt: string;
}

/**
 * useThreadHistory manages paginated fetching of root turns for the chat
 * history popover. Provides `loadInitial` (reset + fetch first page),
 * `loadMore` (next page), and `cancelFetch` (abort in-flight requests).
 */
export function useThreadHistory() {
  const {params} = useNavContext();
  const client = useWbAgentClient(params.entityName, params.projectName);

  const [items, setItems] = useState<ThreadHistoryItem[]>([]);
  const [status, setStatus] = useState<'idle' | 'loading' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);

  const abortRef = useRef<AbortController | null>(null);
  const isFetchingRef = useRef(false);
  const offsetRef = useRef(0);

  // Abort any in-flight request on unmount (e.g. navigation away) to avoid
  // firing state updates into a dead component tree.
  useEffect(() => () => abortRef.current?.abort(), []);

  const fetchPage = useCallback(
    async (offset: number) => {
      if (!client) {
        return;
      }

      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      isFetchingRef.current = true;

      setStatus('loading');
      setError(null);

      try {
        // TODO: Offset based pagination may result in duplicates or missing
        // results. Add backend support for cursor-based pagination or filtering
        // by a timestamp to resolve.
        const turns = await client.get(
          '/turns',
          {
            is_root: true,
            sort_by: 'updated_at',
            sort_desc: true,
            limit: PAGE_SIZE + 1,
            offset,
          },
          controller.signal
        );

        if (controller.signal.aborted) {
          return;
        }

        const hasMoreResults = turns.length > PAGE_SIZE;
        setHasMore(hasMoreResults);

        const newItems = turns.slice(0, PAGE_SIZE).map(turnToItem);
        setItems(prev => {
          const merged = offset === 0 ? newItems : [...prev, ...newItems];

          // Work around duplication problem of offset-based pagination.
          return deduplicateItems(merged);
        });

        offsetRef.current = offset + newItems.length;
        setStatus('idle');
      } catch (err) {
        if ((err as Error).name === 'AbortError') {
          return;
        }

        setError(extractErrorMessage(err));
        setStatus('error');
      } finally {
        // Don't reset if this request was aborted — a newer fetchPage call
        // may have already set isFetchingRef and this must not overwrite it.
        if (!controller.signal.aborted) {
          isFetchingRef.current = false;
        }
      }
    },
    [client]
  );

  const cancelFetch = useCallback(() => abortRef.current?.abort(), []);

  const loadInitial = useCallback(() => {
    offsetRef.current = 0;
    setItems([]);
    setHasMore(true);
    fetchPage(0);
  }, [fetchPage]);

  const loadMore = useCallback(() => {
    if (isFetchingRef.current || !hasMore) {
      return;
    }

    fetchPage(offsetRef.current);
  }, [fetchPage, hasMore]);

  return {
    items,
    status,
    error,
    hasMore,
    loadInitial,
    loadMore,
    cancelFetch,
  };
}

function deduplicateItems(items: ThreadHistoryItem[]): ThreadHistoryItem[] {
  const seen = new Set<string>();
  return items.filter(item => {
    if (seen.has(item.rootTurnId)) {
      return false;
    }

    seen.add(item.rootTurnId);
    return true;
  });
}

function turnToItem(turn: TurnResponse): ThreadHistoryItem {
  return {
    rootTurnId: turn.id,
    title:
      decodePromptForThreadTitle(turn.user_prompt ?? '') ||
      'Untitled conversation',
    updatedAt: turn.updated_at,
  };
}
